import React, { useState, useEffect } from 'react';
import './App.css'; 
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import ApplicationForm from './Pages/ApplicationForm.tsx';
import Navbar from './Components/Navbar';
import Fibre from './Pages/Fibre.tsx';
import AboutUs from './Pages/AboutUs.tsx';
import Contact from './Pages/Contact.tsx';
import BannerSection from './Components/BannerSection';
import Footer from './Components/Footer';
import About from './Components/About';
import Help from './Pages/Help.tsx';
import Profile from './Pages/Profile.tsx';
import ManageSubscription from './Pages/ManageSubscription.tsx';
import ManagePackage from './Pages/ManagePackage.tsx';
import LoginModal from './Components/LoginModal';
import RFC from './Components/RFC';
import { GlobalErrorProvider } from './Components/GlobalErrorContext';
import { GlobalSuccessProvider } from './Components/GlobalSuccessContext';
import { useGlobalError } from './Components/GlobalErrorContext';
import { useGlobalSuccess } from './Components/GlobalSuccessContext';
import Settings from './Pages/Settings.tsx';
import Privacy from './Pages/Privacy.tsx';
import Terms from './Pages/Terms.tsx'; 
import SuccessPage from './Pages/SuccessPage.tsx'; 
import ErrorPage from './Pages/ErrorPage.tsx'; 
import PendingPage from './Pages/PendingPage.tsx';
import PayLater from './Pages/PayLater.tsx'; 
import Referral from './Pages/Referral.tsx';
import ReferralPreferences from './Pages/ReferralPreferences.tsx';
import ReferralHistory from './Pages/ReferralHistory.tsx';
import Feedback from './Pages/Feedback.tsx';

// Helper component to handle legacy or incoming /login?redirect=... requests
//
// FIX: this previously built `isLoggedIn` and `redirectTarget` and then
// returned nothing at all (both `return` statements were commented out),
// so anyone hitting /login or /login?redirect=... saw a blank page and
// never actually got redirected anywhere. Restored the redirect: logged-in
// visitors with a redirect target go straight there, everyone else goes
// home, carrying the intended destination in `?next=` so AppContent's
// handleLoginSuccess (below) can resume the flow after they log in.
const LoginRedirectHandler = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectTarget = params.get('redirect');

  // Check authentication status from localStorage — guarded, since
  // storage access can throw in private mode / ITP (see Referral.tsx,
  // which had to add the same guard).
  let isLoggedIn = false;
  try {
    const accessToken = localStorage.getItem('accessToken');
    const portalEndCustomerId = localStorage.getItem('portalEndCustomerId');
    isLoggedIn = Boolean(accessToken && portalEndCustomerId);
  } catch (err) {
    console.error(
      'LoginRedirectHandler: localStorage access blocked (private mode / ITP):',
      err
    );
  }

  // Forward to target if logged in; otherwise go home but remember
  // where the user was trying to go, so login can resume the flow
  // instead of silently dropping the intended destination.
  if (isLoggedIn && redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  return (
    <Navigate
      to={redirectTarget ? `/?next=${encodeURIComponent(redirectTarget)}` : '/'}
      replace
    />
  );
};

const HomeWrapper = ({ children }) => {
  const location = useLocation();
  const { showSuccessModal } = useGlobalSuccess();
  const { showErrorModal } = useGlobalError();
  const [handled, setHandled] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get('payment');

    if (!handled && paymentStatus) {
      if (paymentStatus === 'success') {
        showSuccessModal('Your payment was successful 🎉');
      } else if (paymentStatus === 'failed') {
        showErrorModal('Payment failed. Please try again.');
      }

      setHandled(true);

      // Clean up URL (remove ?payment=...)
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 2000);
    }
  }, [location.search, handled, showSuccessModal, showErrorModal]);

  return <>{children}</>;
};

// Everything that needs router context (useNavigate) lives here, so it
// can be rendered inside <BrowserRouter> while App itself stays simple.
const AppContent = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  // const navigate = useNavigate();

  const handleLoginClick = () => setShowLogin(true);
  const handleCloseLogin = () => setShowLogin(false);

  // Called by LoginModal after a successful login. If the user arrived
  // via /login?redirect=... and got bounced to /?next=..., this sends
  // them on to where they actually wanted to go.
  return (
    <>
      {/* Top Navbar */}
      <Navbar
        handleLoginClick={handleLoginClick}
      />

      {/* Routes */}
      <Routes>
        {/* Matches /login and /login?redirect=... without firing route warnings */}
        <Route path="/login*" element={<LoginRedirectHandler />} />

        <Route path="/help" element={<Help />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/manage-subscription" element={<ManageSubscription />} />
        <Route path="/application-form-page" element={<ApplicationForm />} />
        <Route path="/manage-package/:subscriptionId" element={<ManagePackage />} />
        <Route path="/fibre" element={<Fibre />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="/pending" element={<PendingPage />} />
        <Route path="/pay-later" element={<PayLater />} />

        {/*
          Referral now owns its own auth flow (ReferralLoginModal). It no
          longer needs onShowLoginModal — the page shows its login form
          inline when the person isn't authenticated, which is exactly
          what QR-code visitors hit first.
        */}
        <Route path="/referral" element={<Referral />} />

        <Route path="/referral-preferences" element={<ReferralPreferences />} />
        <Route path="/referral-history" element={<ReferralHistory />} />
        <Route path="/feedback" element={<Feedback />} />

        {/* Catch-all: Redirect unknown paths to Home */}
        {/* <Route path="*" element={<Navigate to="/" replace />} /> */}

        <Route
          path="/"
          element={
            <HomeWrapper>
              <>
                <div className='full-width-block'>
                  <p>
                    <b>Super Fast Affordable Fibre to your Home is Here</b>
                  </p>
                </div>
                <div className="container-fluid p-0">
                  <img
                    src="ftthsa-homepage-banner.png"
                    alt="Fibre Banner"
                    className="img-fluid w-100"
                  />
                </div>
                <RFC onShowLoginModal={() => setShowLogin(true)} />
                {showLoginModal && <LoginModal handleClose={() => setShowLoginModal(false)} />}
                <Fibre />
                <About />
                <BannerSection />
              </>
            </HomeWrapper>
          }
        />
      </Routes>

      <Footer />

      {/* Global Login Modal */}
      {showLogin && (
        <LoginModal
          handleClose={handleCloseLogin}
        />
      )}
    </>
  );
};

const App = () => {
  return (
    <GlobalErrorProvider>
      <GlobalSuccessProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </GlobalSuccessProvider>
    </GlobalErrorProvider>
  );
};

export default App;